// src/components/OrgMemberPicker.jsx
//
// Searchable combobox of organization members. Used wherever the user has
// to nominate another teammate (notify recipients, share targets, etc) so:
//   - typos are impossible (you only get to pick a real member)
//   - the backend's "must belong to your org" rule is enforced at the UI
//   - users see who they can pick without guessing
//
// The org-members list is fetched once per session and held in a
// module-level promise cache, so opening the picker from multiple sites in
// a single session only triggers one network call.
//
import { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";

const ORG_USERS_URL =
  "https://whiteintel.io/api/regular/app/v2/organizations/get_users.php";

// Session-scoped cache so multiple pickers share one fetch.
let cachedMembers = null;
let cachedPromise = null;

async function loadMembers(jwtToken) {
  if (cachedMembers) return cachedMembers;
  if (cachedPromise) return cachedPromise;
  cachedPromise = (async () => {
    try {
      const res = await fetch(ORG_USERS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      cachedMembers = data?.users || [];
      return cachedMembers;
    } catch (e) {
      cachedPromise = null; // allow a retry on next mount after a failure
      throw e;
    }
  })();
  return cachedPromise;
}

export default function OrgMemberPicker({
  value,
  onChange,
  jwtToken,
  enabled = true,
  excludeEmail,
  placeholder = "Select a teammate…",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState(() => cachedMembers || []);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Fetch members when the picker becomes enabled (typically when the
  // multi-notify-allowed gate flips on and the org is known to exist).
  useEffect(() => {
    if (!enabled || !jwtToken) return;
    if (cachedMembers) {
      setMembers(cachedMembers);
      return;
    }
    setLoading(true);
    loadMembers(jwtToken)
      .then(setMembers)
      .catch((e) => console.error("OrgMemberPicker fetch error:", e))
      .finally(() => setLoading(false));
  }, [enabled, jwtToken]);

  // Close on outside click + ESC. We reset the query so the next open
  // starts from the selected value, not whatever the user half-typed before.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const exclude = (excludeEmail || "").toLowerCase();
    return members
      .filter((m) => m && typeof m.email === "string" && m.email.length > 0)
      .filter((m) => !exclude || m.email.toLowerCase() !== exclude)
      .filter((m) => !q || m.email.toLowerCase().includes(q));
  }, [members, query, excludeEmail]);

  const select = (email) => {
    onChange(email);
    setQuery("");
    setOpen(false);
  };

  // While the picker is open we show the query so the user can keep typing;
  // while closed we show whatever they actually picked.
  const inputValue = open ? query : (value || "");

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full rounded-md text-[13px] py-2 pl-3 pr-14 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {value && !disabled && (
            <button
              type="button"
              onClick={() => { onChange(""); setQuery(""); }}
              title="Clear"
              className="p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => !disabled && setOpen((v) => !v)}
            disabled={disabled}
            tabIndex={-1}
            className="p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10 disabled:opacity-50"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-[240px] overflow-y-auto rounded-md border border-sky-900/40 bg-[#0a1224] shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
          {loading ? (
            <div className="px-3 py-2.5 text-[12px] text-neutral-500">Loading members…</div>
          ) : filteredMembers.length === 0 ? (
            <div className="px-3 py-2.5 text-[12px] text-neutral-500">
              {query.trim()
                ? "No matches."
                : members.length === 0
                  ? "No other members in your organization. Invite teammates from Organization Settings."
                  : "No members available."}
            </div>
          ) : (
            <ul>
              {filteredMembers.map((m) => {
                const isActive = value && value.toLowerCase() === m.email.toLowerCase();
                const isAdmin = (m.organization_role || "").toLowerCase() === "admin";
                return (
                  <li key={m.email}>
                    <button
                      type="button"
                      onClick={() => select(m.email)}
                      className={`w-full text-left px-3 py-2 text-[12.5px] flex items-center justify-between gap-2 transition-colors ${
                        isActive
                          ? "bg-sky-500/15 text-sky-100"
                          : "text-neutral-200 hover:bg-sky-500/10"
                      }`}
                    >
                      <span className="font-mono truncate min-w-0">{m.email}</span>
                      {m.organization_role && (
                        <span
                          className={`inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset shrink-0 ${
                            isAdmin
                              ? "bg-sky-500/10 text-sky-300 ring-sky-500/30"
                              : "bg-neutral-500/10 text-neutral-400 ring-neutral-500/30"
                          }`}
                        >
                          {m.organization_role}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Test-only helper so caches don't leak across hot-reload boundaries during
// development. Safe to leave unused in production.
export function _resetOrgMemberCache() {
  cachedMembers = null;
  cachedPromise = null;
}
